import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { StatCard } from "../components/admin/AdminUI";
import { AdminEvents } from "../components/admin/AdminEvents";
import { AdminProposals } from "../components/admin/AdminProposals";
import { EventFormDialog } from "../components/admin/EventFormDialog";
import { OccurrencesDialog } from "../components/admin/OccurrencesDialog";

const empty = { title: "", description: "", category: "DJ Set", neighborhood: "Navigli", date: "", time: "", price: "", image_url: "", address: "", neighborhood: "", zone_text: "", venue_name: "", end_time: "", recurrence_freq: "none", recurrence_until: "" };

export default function Admin() {
  const [section, setSection] = useState("events");
  const [events, setEvents] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const loadEvents = useCallback(() => api.get("/events", { params: { include_past: true } }).then((r) => setEvents(r.data)).catch(() => {}), []);
  const loadProposals = useCallback(() => api.get("/admin/proposals").then((r) => setProposals(r.data)).catch(() => {}), []);
  useEffect(() => { loadEvents(); loadProposals(); }, [loadEvents, loadProposals]);

  const openNew = () => { setEditing(null); setReviewing(null); setForm(empty); setDialogOpen(true); };
  const [reviewing, setReviewing] = useState(null);
  const [datesFor, setDatesFor] = useState(null);
  const confirmSeries = async (id) => {
    try { await api.post(`/admin/events/${id}/confirm`); toast.success("Serata confermata"); loadEvents(); }
    catch { toast.error("Operazione fallita"); }
  };
  const archiveSeries = async (id) => {
    if (!window.confirm("Archiviare questa serata? Non sarà più visibile sul sito.")) return;
    try { await api.post(`/admin/events/${id}/archive`); setEvents((e) => e.filter((x) => x.id !== id)); toast.success("Serata archiviata"); }
    catch { toast.error("Operazione fallita"); }
  };
  const toForm = (e) => ({ ...empty, title: e.title || "", description: e.description || "", category: e.category || "Chill", neighborhood: e.neighborhood || "", zone_text: e.zone_text || "", venue_name: e.venue_name || "", date: e.series_start || e.date || "", time: e.time || "", end_time: e.end_time || "", price: e.price || "", image_url: e.image_url || "", address: e.address || "", recurrence_freq: e.recurrence_freq || "none", recurrence_until: e.recurrence_until || "" });
  const openReview = (p) => { setEditing(null); setReviewing(p.id); setForm(toForm(p.event_data)); setDialogOpen(true); };
  const openEdit = (e) => {
    setReviewing(null);
    setEditing(e.id);
    setForm(toForm(e));
    setDialogOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    try {
      if (reviewing) {
        await api.post(`/admin/proposals/${reviewing}/publish`, form);
        toast.success("Proposta approvata e pubblicata");
        setProposals((p) => p.map((x) => (x.id === reviewing ? { ...x, status: "approved" } : x)));
      } else if (editing) { await api.put(`/admin/events/${editing}`, form); toast.success("Evento aggiornato"); }
      else { await api.post("/admin/events", form); toast.success("Evento creato"); }
      setDialogOpen(false); loadEvents();
    } catch (err) { toast.error(err.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;
    try { await api.delete(`/admin/events/${id}`); setEvents((e) => e.filter((x) => x.id !== id)); toast.success("Evento eliminato"); }
    catch { toast.error("Eliminazione fallita"); }
  };

  const moderate = async (id, status) => {
    try {
      await api.put(`/admin/proposals/${id}/status`, { status });
      setProposals((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(status === "approved" ? "Proposta approvata e pubblicata" : "Proposta rifiutata");
      if (status === "approved") loadEvents();
    } catch { toast.error("Operazione fallita"); }
  };

  const pendingCount = proposals.filter((p) => p.status === "pending").length;

  return (
    <div className="pt-24 max-w-7xl mx-auto px-5 pb-16">
      <div className="overline text-wyd-accent mb-2">Pannello Admin</div>
      <h1 className="font-display font-black text-4xl mb-8">Gestione WYD</h1>

      <div className="grid grid-cols-2 gap-4 mb-10 max-w-xl">
        <StatCard testid="admin-tab-events" label="Eventi" value={events.length} active={section === "events"} onClick={() => setSection("events")} accent="text-white" />
        <StatCard testid="admin-tab-proposals" label="Proposte da moderare" value={pendingCount} active={section === "proposals"} onClick={() => setSection("proposals")} accent={pendingCount > 0 ? "text-wyd-accent" : "text-white"} />
      </div>

      {section === "events"
        ? <AdminEvents events={events} onNew={openNew} onEdit={openEdit} onDelete={remove} onDates={setDatesFor} onConfirm={confirmSeries} onArchive={archiveSeries} />
        : <AdminProposals proposals={proposals} onModerate={moderate} onReview={openReview} />}

      <OccurrencesDialog event={datesFor} onClose={() => setDatesFor(null)} onChanged={loadEvents} />
      <EventFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} reviewing={!!reviewing} form={form} set={set} onSubmit={save} saving={saving} />
    </div>
  );
}
